/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Description, Textarea, Text, theme } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { useAuth0 } from "~/components/Auth/auth";
import { Val } from "~/components/InlineEdit/InlineComment";
import { Comment } from "~/models";
import { useClickOutside } from "~/tools";

type CommentFormProps = {
  newComment: boolean;
  isEditMode?: boolean;
  setIsEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (values: Val) => void;
  item?: Comment;
  property: "text";
  height?: number;
  isLoading?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function CommentForm({
  newComment,
  property,
  isEditMode,
  setIsEditMode,
  onSubmit,
  item,
  height,
  isLoading,
}: CommentFormProps) {
  const [length, setLength] = React.useState<number>(0);
  const wrapperRef = React.useRef(null);

  const { register, handleSubmit, errors, formState, watch, reset } = useForm();
  const watcher = watch();

  useClickOutside(
    wrapperRef,
    () => {
      if (setIsEditMode) {
        !newComment && setIsEditMode(false);
      }
    },
    isEditMode
  );

  React.useEffect(() => {
    if (item) {
      !newComment && setLength(item[property].length);
    }
  }, [item, newComment, property]);

  React.useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset({ [property]: "" });
      setLength(0);
    }
  }, [formState, property, reset]);

  const { user } = useAuth0();

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      css={{ display: "block" }}
      ref={wrapperRef}
    >
      <Textarea
        onChange={(e) => {
          setLength(e.target.value.length);
        }}
        type="text"
        data-cy="text-input"
        name={property}
        status={errors[property] && "danger"}
        ref={register({
          required: {
            value: true,
            message: "This field cannot be empty",
          },
          minLength: {
            value: 5,
            message: "The minimum number of characters is 5.",
          },
          maxLength: {
            value: 1000,
            message: "The maximum number of characters is 1000.",
          },
        })}
        defaultValue={item ? item[property] : ""}
        placeholder={
          newComment ? `Commenting publicly as ${user?.email}...` : ""
        }
        css={{ height: height ? Math.floor(height) : "6rem" }}
      />
      <FlexContainer
        justify="space-between"
        align="center"
        css={{ marginTop: "0.5rem" }}
      >
        <Text
          size="sm"
          color={
            length > 950 ? theme.color.text.warning : theme.color.text.text01
          }
        >
          {length} / 1000
        </Text>

        <FlexContainer>
          {!newComment ? (
            <>
              <Button
                initialState={isLoading ? "loading" : ""}
                type="submit"
                icon="save"
                size="md"
                severity="low"
                disabled={!watcher.text}
              >
                Save
              </Button>
              <Button
                ml=".5rem"
                icon="close"
                size="md"
                severity="low"
                onClick={() => {
                  setIsEditMode && setIsEditMode(false);
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              size="md"
              initialState={isLoading ? "loading" : ""}
              disabled={!watcher.text}
            >
              Save
            </Button>
          )}
        </FlexContainer>
      </FlexContainer>
      {errors[property] && <Description>{errors.text.message}</Description>}
    </form>
  );
}

export { CommentForm };
